// "Schedule an operation" — the doctor's provisional OT booking.
//
// This is the trigger the OT module was missing. Until it existed OT only
// pulled: a case was created from inside OT and nothing outside could start the
// process, so a surgeon who had just decided to operate had no way to say so.
//
// WHAT THIS DOES NOT DO is pick a theatre. The doctor cannot see the theatre
// diary and should not be guessing at it, so the date and time here are a
// PREFERENCE. OT books the room and the real time; both are kept, and this
// screen shows the difference once they have.
//
// UNLIKE THE OTHER SECTIONS, this one posts when you press the button rather
// than accumulating into the prescription save. Sending a patient to theatre is
// an action with a consequence in another department, not a note on a
// prescription — and it has to reach OT whether or not the consultation is
// finished. Moving it into the prescription save would mean editing that
// shared path; say so if that is what you want instead.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";

import {
  fetchOtRequests,
  raiseOtRequest,
  withdrawOtRequest,
} from "../../ot/api/otApi";
import { formatIstDateTime } from "../../../utils/istDateTime";
import OtDaySchedule from "./OtDaySchedule";
import ChargeServiceAutocomplete from "../../ot-charge-capture/ChargeServiceAutocomplete";
import { CLASSIFICATION_OT_SURGERIES } from "../../ot/constants";

const SCHEDULING_TYPES = [
  { value: "ELECTIVE", label: "Elective" },
  { value: "EMERGENCY", label: "Emergency" },
  { value: "DAYCARE", label: "Daycare" },
];

const LABEL_SX = { fontSize: 12, fontWeight: 600, color: "#64748B", mb: 0.5 };

function Field({ label, children, span = 1 }) {
  return (
    <Box sx={{ gridColumn: `span ${span}` }}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}

/** How far OT's booking landed from what was asked for, in words. */
function driftLabel(minutes) {
  if (!minutes) return null;
  const abs = Math.abs(minutes);
  const span = abs >= 60 ? `${Math.floor(abs / 60)}h${abs % 60 || ""}` : `${abs} min`;
  return `${span} ${minutes > 0 ? "later" : "earlier"} than you asked`;
}

export default function OtRequestSection({ patientId, doctorData, diagnosisText }) {
  const blank = {
    procedure_name: "",
    medical_service_id: "",
    requested_date: moment().add(1, "day").format("YYYY-MM-DD"),
    requested_start_time: "09:00",
    estimated_duration_min: "",
    scheduling_type: "ELECTIVE",
    notes: "",
  };
  const [form, setForm] = useState(blank);
  // The chosen catalogue row, kept whole so the picker shows it back.
  const [service, setService] = useState(null);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const body = await fetchOtRequests({ mine: 1 });
      // This patient's requests only — the section is about the person in front
      // of the doctor, not their whole list.
      setMine((body?.requests || []).filter((r) => r.patient_id === patientId));
    } catch (err) {
      setMine([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) load();
    else setLoading(false);
  }, [patientId, load]);

  const send = async () => {
    if (!form.procedure_name.trim()) {
      toast.error("Say which operation is being asked for");
      return;
    }
    setBusy(true);
    try {
      const res = await raiseOtRequest({
        patient_id: patientId,
        procedure_name: form.procedure_name.trim(),
        // The catalogue link, so OT does not have to guess which surgery this
        // is when it comes to bill it.
        medical_service_id: form.medical_service_id || undefined,
        requested_date: form.requested_date,
        requested_start_time: form.requested_start_time || undefined,
        estimated_duration_min: form.estimated_duration_min || undefined,
        scheduling_type: form.scheduling_type,
        notes: form.notes.trim() || undefined,
        surgeon_id: doctorData?.doctor_id,
        surgeon_name: doctorData?.name,
        // Carried from the consultation so OT never asks for it again. Snapshot,
        // not a live link: the record has to say what was believed when the
        // decision to operate was made.
        diagnosis: diagnosisText || undefined,
        diagnosis_source: diagnosisText ? doctorData?.name || "This consultation" : undefined,
      });
      if (!res.ok) {
        toast.error(res.message || "Could not send it to theatre");
        return;
      }
      toast.success(res.message || "Sent to the operation theatre");
      (res.warnings || []).forEach((w) => toast.info(w));
      setForm(blank);
      setService(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (row) => {
    const reason = window.prompt("Why is this being withdrawn?");
    if (!reason || !reason.trim()) return;
    const res = await withdrawOtRequest(row.id, reason.trim());
    if (res.ok) {
      toast.success(res.message || "Withdrawn");
      load();
    } else {
      toast.error(res.message || "Could not withdraw it");
    }
  };

  if (!patientId) {
    return (
      <Typography sx={{ fontSize: 13, color: "#64748B" }}>
        Choose a patient before scheduling an operation.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        {/* The operation was free text, so `ot_request.medical_service_id` was
            never filled and the catalogue link was lost at the one moment it
            was known — the surgeon choosing the operation. Picking it here
            carries the service all the way to the case procedure and the
            surgery charge. Typing something the catalogue lacks still works;
            the name is kept and only the link is missing. */}
        <Field label="Operation" span={2}>
          <ChargeServiceAutocomplete
            placeholder="Search the surgery catalogue…"
            classificationName={CLASSIFICATION_OT_SURGERIES}
            value={service}
            onChange={(item) => {
              setService(item);
              setForm((f) => ({
                ...f,
                procedure_name: item ? item.name : "",
                medical_service_id: item ? item.medical_service_id || item.id : "",
              }));
            }}
          />
        </Field>
        <Field label="Type">
          <TextField
            select
            fullWidth
            size="small"
            value={form.scheduling_type}
            onChange={(e) => setForm((f) => ({ ...f, scheduling_type: e.target.value }))}
          >
            {SCHEDULING_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Field>
        <Field label="Roughly how long">
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="minutes"
            value={form.estimated_duration_min}
            onChange={(e) =>
              setForm((f) => ({ ...f, estimated_duration_min: e.target.value }))
            }
          />
        </Field>

        <Field label="Wanted on">
          <TextField
            fullWidth
            size="small"
            type="date"
            value={form.requested_date}
            onChange={(e) => setForm((f) => ({ ...f, requested_date: e.target.value }))}
          />
        </Field>
        <Field label="Preferred time">
          <TextField
            fullWidth
            size="small"
            type="time"
            value={form.requested_start_time}
            onChange={(e) =>
              setForm((f) => ({ ...f, requested_start_time: e.target.value }))
            }
          />
        </Field>
        {/* The diary for the day being asked for, so the time below is a
            choice rather than a guess. It follows requested_date. */}
        <Field label="What theatre looks like that day" span={2}>
          <OtDaySchedule date={form.requested_date} />
        </Field>
        <Field label="Anything theatre should know" span={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="image intensifier, implant size, special kit…"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </Field>
      </Box>

      <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button variant="contained" size="small" disabled={busy} onClick={send}>
          Send to theatre
        </Button>
        <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
          The time is a preference — theatre will book the room and confirm.
          {diagnosisText ? " Your diagnosis goes with it." : ""}
        </Typography>
      </Box>

      {loading ? (
        <CircularProgress size={18} sx={{ mt: 2 }} />
      ) : (
        mine.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1, letterSpacing: "0.03em" }}>
              ALREADY ASKED FOR
            </Typography>
            {mine.map((r) => {
              const drift = driftLabel(r.minutes_from_request);
              return (
                <Box
                  key={r.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    py: 1,
                    borderBottom: "1px solid #E2E8F0",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 180 }}>
                    {r.procedure_name}
                  </Typography>

                  {r.status === "SCHEDULED" ? (
                    <>
                      <Chip
                        size="small"
                        label={`Booked ${formatIstDateTime(r.booked_start_datetime)}`}
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#E6F4EA",
                          color: "#1E7E34",
                          border: "1px solid #A5D6A7",
                        }}
                      />
                      {r.operating_room_name && (
                        <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                          {r.operating_room_name}
                        </Typography>
                      )}
                      {/* The gap is stated rather than left for the doctor to
                          work out from two timestamps. */}
                      {drift && (
                        <Typography sx={{ fontSize: 12, color: "#B45309" }}>{drift}</Typography>
                      )}
                    </>
                  ) : r.status === "WITHDRAWN" ? (
                    <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
                      Withdrawn — {r.withdrawn_reason}
                    </Typography>
                  ) : (
                    <>
                      <Chip
                        size="small"
                        label="Waiting for theatre"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: "#FFF8E1",
                          color: "#B45309",
                          border: "1px solid #FFE082",
                        }}
                      />
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        asked for {formatIstDateTime(r.requested_datetime)}
                      </Typography>
                      <Button size="small" color="error" onClick={() => withdraw(r)}>
                        Withdraw
                      </Button>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        )
      )}
    </Box>
  );
}
